/**
 * Promoted Blueprint Service
 * Handles promoted blueprint-related API calls (knowledge + workflow tracks)
 */

import api from '@/lib/api';
import { withBuScope } from '@/lib/business-unit-scope';
import { API_ENDPOINTS } from '@/constants/endpoints';
import { PromotedBlueprintAdapter } from '@/adapters/promotedBlueprint.adapter';
import type { ApiResponse } from '@/types';
import type { TemplateReadiness, TemplateTypeLink } from '@/modules/back-testing/readiness/types';
import type {
  BlueprintAutomation,
  KnowledgeBlueprint,
  WorkflowBlueprint,
  PromotedBlueprintTrack,
  KnowledgeBlueprintResponseDto,
  WorkflowBlueprintResponseDto
} from '@/modules/promoted-blueprints/types';

export const promotedBlueprintService = {
  /**
   * List knowledge blueprints
   */
  async listKnowledge(scope: { bu_id?: string } = {}): Promise<KnowledgeBlueprint[]> {
    const response = await api.get<ApiResponse<{ items: KnowledgeBlueprintResponseDto[]; count: number }>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.LIST, {
      params: { track: 'knowledge', ...scope }
    });
    return PromotedBlueprintAdapter.fromKnowledgeApiList(response.data.data.items);
  },

  /**
   * List workflow blueprints (workflow track)
   */
  async listWorkflow(scope: { bu_id?: string } = {}): Promise<WorkflowBlueprint[]> {
    const response = await api.get<ApiResponse<{ items: WorkflowBlueprintResponseDto[]; count: number }>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.LIST, {
      params: { track: 'workflow', ...scope }
    });
    return PromotedBlueprintAdapter.fromWorkflowApiList(response.data.data.items);
  },

  /**
   * Get a single workflow blueprint by ID
   */
  async getWorkflowById(id: string): Promise<WorkflowBlueprint> {
    const response = await api.get<ApiResponse<WorkflowBlueprintResponseDto>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.BY_ID(id), {
      params: { track: 'workflow' }
    });
    return PromotedBlueprintAdapter.fromWorkflowApi(response.data.data);
  },

  /**
   * Toggle enabled state for a knowledge blueprint or workflow blueprint
   */
  async setEnabled(id: string, track: PromotedBlueprintTrack, enabled: boolean): Promise<void> {
    await api.patch<ApiResponse<null>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.ENABLED(id), { track, enabled });
  },

  /**
   * Toggle enabled state for a whole knowledge intent (enables/disables all of its
   * queries at once, identified by the shared intent key).
   */
  /**
   * Keyed by INTENT, not by document id, so the brand has to travel with it: without one
   * the backend matches every brand's docs for that intent and disables them all.
   */
  async setKnowledgeIntentEnabled(
    intent: string,
    enabled: boolean,
    buScope: { bu_id?: string } = {},
  ): Promise<void> {
    await api.patch<ApiResponse<null>>(
      withBuScope(API_ENDPOINTS.PROMOTED_BLUEPRINTS.KNOWLEDGE_INTENT_ENABLED(intent), buScope),
      { enabled },
    );
  },

  /**
   * Toggle auto-send state for a knowledge blueprint (knowledge track only)
   */
  async setAutoSend(id: string, autoSend: boolean): Promise<void> {
    await api.patch<ApiResponse<null>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.AUTO_SEND(id), { track: 'knowledge', autoSend });
  },

  /**
   * Toggle enabled state for a single template within a workflow blueprint (workflow only)
   */
  async setTemplateEnabled(id: string, index: number, enabled: boolean): Promise<void> {
    await api.patch<ApiResponse<null>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.TEMPLATE_ENABLED(id, index), { enabled });
  },

  /**
   * Each template with the scenario type it answers and that type's back-testing verdict
   */
  async getTemplateReadiness(id: string): Promise<TemplateReadiness[]> {
    const response = await api.get<ApiResponse<{ templates: TemplateReadiness[] }>>(
      API_ENDPOINTS.PROMOTED_BLUEPRINTS.TEMPLATE_READINESS(id)
    );
    return response.data.data.templates;
  },

  /**
   * Link a template to the scenario type it answers, or unlink it with null
   */
  async setTemplateTypeLink(id: string, index: number, link: TemplateTypeLink | null): Promise<TemplateReadiness[]> {
    const response = await api.put<ApiResponse<{ templates: TemplateReadiness[] }>>(
      API_ENDPOINTS.PROMOTED_BLUEPRINTS.TEMPLATE_TYPE_LINK(id, index),
      { link }
    );
    return response.data.data.templates;
  },

  /**
   * The workflow's tool steps and whether each runs without supervisor approval
   */
  async getAutomation(id: string): Promise<BlueprintAutomation> {
    const response = await api.get<ApiResponse<BlueprintAutomation>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.AUTOMATION(id));
    return response.data.data;
  },

  /**
   * Replace the set of tool steps that run without supervisor approval
   */
  async setAutomation(id: string, stepIds: string[]): Promise<BlueprintAutomation> {
    const response = await api.put<ApiResponse<BlueprintAutomation>>(API_ENDPOINTS.PROMOTED_BLUEPRINTS.AUTOMATION(id), {
      stepIds
    });
    return response.data.data;
  },

  /**
   * Switch every linked template on or off to match its type's back-testing verdict
   */
  async applyReadiness(id: string): Promise<{ enabled: number[]; disabled: number[]; templates: TemplateReadiness[] }> {
    const response = await api.post<
      ApiResponse<{ enabled: number[]; disabled: number[]; templates: TemplateReadiness[] }>
    >(API_ENDPOINTS.PROMOTED_BLUEPRINTS.APPLY_READINESS(id));
    return response.data.data;
  }
};
